import { useContainerLayout, useTheme } from '@chronosphereio/chrono-ui';
import { useMemo } from 'react';
import { useVizContainerContext } from '../../context/VizContainerContextProvider';

export interface UseVizContainerLayoutProps {}

// Note: this hook primarily returns numbers because we will eventually need them to do math to
// support backfilling `VizPanel` to use `VizContainer`.
export interface UseVizContainerLayoutResponse {
  /**
   * Layout to apply to the `Container`.
   */
  container: {
    paddingY: number;
    paddingX: number;
  };

  /**
   * Layout to apply to the `Container` body's inner div.
   */
  containerBodyInner: {
    paddingY: number;
    paddingX: number;
    borderBottomLeftRadius: number;
    borderBottomRightRadius: number;
  };

  /**
   * Layout to apply to the `Container`'s header.
   */
  header: {
    marginY: number;
    marginX: number;
  };
}

/**
 * Hook that provides some constants related to the layout of the `VizContainer`.
 */
export const useVizContainerLayout = (): UseVizContainerLayoutResponse => {
  const { theme } = useTheme();
  const { variant, disablePadding } = useVizContainerContext();

  const containerLayout = useContainerLayout({ variant, disablePadding });

  const spacingPixels = parseFloat(theme.spacing(1));

  // Padding that would be used for the specified variant if `noPadding` was NOT enabled. This
  // helps us set fallback padding on the header when `noPadding` is enabled.
  const defaultContainerPadding: UseVizContainerLayoutResponse['container'] = useMemo(() => {
    if (variant === 'subtle') {
      // Override the default `Container` padding only for the `subtle` header variant as per the designs
      return {
        paddingY: 0,
        paddingX: spacingPixels,
      };
    }

    return {
      paddingY: containerLayout.containerDefault.paddingY,
      paddingX: containerLayout.containerDefault.paddingX,
    };
  }, [containerLayout.containerDefault.paddingX, containerLayout.containerDefault.paddingY, spacingPixels, variant]);

  const container: UseVizContainerLayoutResponse['container'] = useMemo(() => {
    if (disablePadding) {
      return containerLayout.container;
    }

    return defaultContainerPadding;
  }, [containerLayout.container, defaultContainerPadding, disablePadding]);

  // Note: we handle the header layout differently in `Container` and `VizContainer` because of some differences in
  // requirements and varying levels of access to the `variant` prop.
  const header: UseVizContainerLayoutResponse['header'] = useMemo(() => {
    if (disablePadding) {
      // When padding is disabled, the header cannot rely on the outer container padding to
      // properly space the header, so we instead need to set it on the header itself. Margins are
      // used instead of padding here because we are generating spacing between the header and
      // the container (not inside the header), and it avoids more complex css issues with padding,
      // height, and box-sizing for the header.
      return {
        marginY: defaultContainerPadding.paddingY,
        marginX: defaultContainerPadding.paddingX,
      };
    }

    return {
      marginY: 0,
      marginX: 0,
    };
  }, [defaultContainerPadding.paddingX, defaultContainerPadding.paddingY, disablePadding]);

  return useMemo(() => {
    return {
      container,
      containerBodyInner: containerLayout.containerBodyInner,
      header,
    };
  }, [container, containerLayout.containerBodyInner, header]);
};
